/**
 * useTicketSearch - Debounced report search for the admin ticket list
 *
 * Extracted from useAdminReports.js. Owns search text state and the
 * embedding-search flow, including:
 * - Direct UUID lookup when a full report ID is pasted
 * - Per-org embedding search honoring the active view/toggles/filters
 * - Debounce + response versioning so stale responses are discarded
 *
 * @param {string} organization - Base organization from route params
 * @param {Object} deps
 * @param {Object} deps.filtersHook - useReportFilters instance (org/HRO/KPI filters)
 * @param {string} deps.ticketView - Current ticket view key
 * @param {boolean} deps.includeAssigned - "All Open" include-assigned toggle
 * @param {boolean} deps.showOlderTickets - Disable the default 30-day window
 * @param {boolean} deps.includePreviousReports - Unresponded-only toggle
 * @param {string} deps.dateRangeStart - Custom range start ('YYYY-MM-DD')
 * @param {string} deps.dateRangeEnd - Custom range end ('YYYY-MM-DD')
 * @param {number} deps.checked - Time filter index (-1 until initialized)
 * @param {Function} deps.generateReports - Regenerate the unsearched report list
 * @param {Function} deps.setReports - Report list setter (from useTicketData)
 * @param {Function} deps.setReportsLoading - Loading setter (from useTicketData)
 * @param {Function} deps.handleError - Error handler from useErrorState
 */

import { useState, useCallback, useRef } from 'react';

import Util from '../util/util.js';
import {
  filterByHRO,
  filterByKPI,
  filterUnrespondedOnly,
  TIME_CONSTRAINED_VIEWS,
  DEFAULT_TIME_CONSTRAINT_MS,
  UUID_REGEX,
  filterByDateRange,
} from '../util/reports';
import { t } from '../i18n';

export default function useTicketSearch(
  organization,
  {
    filtersHook,
    ticketView,
    includeAssigned,
    showOlderTickets,
    includePreviousReports,
    dateRangeStart,
    dateRangeEnd,
    checked,
    generateReports,
    setReports,
    setReportsLoading,
    handleError,
  }
) {
  const [searchText, setSearchText] = useState('');
  const searchVersionRef = useRef(0);
  const searchDebounceRef = useRef(null);

  /**
   * Execute search API call (internal — use searchReports for debounced version)
   */
  const executeSearch = useCallback(async () => {
    // Increment version to discard stale responses from previous searches
    const version = ++searchVersionRef.current;

    setReportsLoading(true);
    try {
      // If the user pasted a full report UUID, fetch that report directly and
      // skip the embedding search + ticket-view filters entirely.
      const trimmed = searchText.trim();
      if (UUID_REGEX.test(trimmed)) {
        const data = await Util.get('Resty', '/items/getFromUuid/' + trimmed);
        if (searchVersionRef.current !== version) return;
        const item = data?.items;
        const report = item && typeof item === 'object' && !Array.isArray(item) && item.uuid ? item : null;
        setReports(report ? [report] : []);
        setReportsLoading(false);
        return;
      }

      // Honor the org-filter dropdown (same pattern as fetchTickets).
      // If the user has selected one or more orgs in the filter, search those;
      // otherwise fall back to the base org from route params.
      const orgsToQuery = filtersHook.OrgFilterValue?.length > 0
        ? filtersHook.OrgFilterValue
        : [organization];

      // Map ticket view to server-side filters
      const viewFilters = {
        myActive:      { status: 'Open,InProgress', assignedToMe: 'true' },
        myClosed:      { status: 'Closed', assignedToMe: 'true' },
        allOpen:       { status: 'Open', ...(includeAssigned ? {} : { unassignedOnly: 'true' }) },
        allInProgress: { status: 'InProgress' },
        allClosed:     { status: 'Closed' },
      };
      const baseFilters = viewFilters[ticketView] || {};

      // Apply time constraint for applicable views
      const isTimeCon = TIME_CONSTRAINED_VIEWS.includes(ticketView);
      const minTimestamp = (isTimeCon && !showOlderTickets)
        ? Date.now() - DEFAULT_TIME_CONSTRAINT_MS
        : null;

      // Fetch one org at a time; the embedding search Lambda is single-org.
      // For multi-org selections we concatenate; per-org top-100 each.
      let mergedItems = [];
      for (const org of orgsToQuery) {
        const queryParams = {
          organization: org,
          searchTerm: searchText,
          ...baseFilters,
          ...(minTimestamp ? { minTimestamp } : {}),
        };

        let data;
        try {
          data = await Util.get('Resty', '/items/searchEmbeddings', { queryParams });
        } catch (firstErr) {
          // Single retry on server error (e.g. Lambda cold start 502)
          const status = firstErr?.status || firstErr?.response?.statusCode;
          if (status >= 500) {
            data = await Util.get('Resty', '/items/searchEmbeddings', { queryParams });
          } else {
            throw firstErr;
          }
        }

        // Discard result if a newer search was initiated while this one was in-flight
        if (searchVersionRef.current !== version) return;

        mergedItems = mergedItems.concat(data.items || []);
      }

      // Apply client-side filters (HRO/KPI/date range/unresponded)
      let results = mergedItems;
      results = filterByHRO(results, filtersHook.HROFilterValue);
      results = filterByKPI(results, filtersHook.KPIFilterValue);
      results = filterByDateRange(results, dateRangeStart, dateRangeEnd);
      if (includePreviousReports) {
        results = filterUnrespondedOnly(results);
      }
      setReports(results);
    } catch (err) {
      if (searchVersionRef.current !== version) return;
      handleError(err, { userMessage: t('reports:list.searchFailed') });
    }
    setReportsLoading(false);
  }, [organization, searchText, ticketView, includeAssigned, showOlderTickets, includePreviousReports, filtersHook.OrgFilterValue, filtersHook.HROFilterValue, filtersHook.KPIFilterValue, dateRangeStart, dateRangeEnd, setReports, setReportsLoading, handleError]);

  /**
   * Search reports by text (debounced — only fires one API call for rapid triggers)
   */
  const searchReports = useCallback(() => {
    // If search text is empty, refresh with current filters instead
    if (!searchText?.trim()) {
      clearTimeout(searchDebounceRef.current);
      if (checked !== -1) {
        generateReports(checked);
      }
      return;
    }

    // Debounce: cancel any pending search and schedule a new one
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      executeSearch();
    }, 300);
  }, [searchText, executeSearch, generateReports, checked]);

  /**
   * Reset search and regenerate reports
   */
  const resetSearch = useCallback(() => {
    setSearchText('');
    clearTimeout(searchDebounceRef.current);
    if (checked !== -1) {
      generateReports(checked);
    }
  }, [checked, generateReports]);

  return {
    searchText,
    setSearchText,
    searchReports,
    resetSearch,
  };
}
